<div class="admin-shell">
    <header class="admin-header">
        <nav class="admin-tabs" aria-label="Admin sections">
            <a role="link" tabindex="0" [class.active]="IsActive('admin-dashboard')" (click)="GoTo('admin-dashboard')" (keydown.enter)="GoTo('admin-dashboard')">Dashboard</a>
            <a role="link" tabindex="0" [class.active]="IsActive('admin-configuration')" (click)="GoTo('admin-configuration')" (keydown.enter)="GoTo('admin-configuration')">Configuration</a>
            <a role="link" tabindex="0" [class.active]="IsActive('admin-partners')" (click)="GoTo('admin-partners')" (keydown.enter)="GoTo('admin-partners')">Partner Links</a>
        </nav>
        <h1>Partner Links</h1>
        <p>Companies are the partner accounts; magic links are their credentials. Revoking a link ends its sessions immediately.</p>
    </header>

    <p *ngIf="isLoading" class="status">Loading…</p>
    <p *ngIf="errorMessage" class="status error" role="alert">{{ errorMessage }}</p>

    <section *ngIf="!isLoading && !errorMessage">
        <form class="new-company" (ngSubmit)="CreateCompany()">
            <label for="new-company-name">New company</label>
            <input
                id="new-company-name"
                name="newCompanyName"
                type="text"
                maxlength="50"
                placeholder="Company name"
                [(ngModel)]="newCompanyName"
                [disabled]="isCreatingCompany" />
            <button type="submit" class="btn primary" [disabled]="isCreatingCompany || newCompanyName.trim().length === 0">
                {{ isCreatingCompany ? 'Creating…' : 'Create company' }}
            </button>
        </form>

        <p *ngIf="companies.length === 0" class="empty">No companies yet — create one above, then issue its link.</p>

        <article class="company-card" *ngFor="let company of companies">
            <header class="company-head">
                <div>
                    <h2>{{ company.companyName }}</h2>
                    <p class="meta">{{ ActiveInvites(company).length }} active link{{ ActiveInvites(company).length === 1 ? '' : 's' }}</p>
                </div>
                <button type="button" class="btn primary" (click)="OpenCreateLink(company)">Create link</button>
            </header>

            <p *ngIf="company.invites.length === 0" class="empty">No links issued yet.</p>

            <table *ngIf="company.invites.length > 0" class="admin-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Expires</th>
                        <th class="num">Uses</th>
                        <th>Created</th>
                        <th class="actions-col"><span class="visually-hidden">Actions</span></th>
                    </tr>
                </thead>
                <tbody>
                    <tr *ngFor="let invite of company.invites">
                        <td class="name-cell">
                            <ng-container *ngIf="renamingInviteId !== invite.inviteId">
                                <span class="link-name">{{ invite.label || '—' }}</span>
                                <span class="link-email" *ngIf="invite.email">{{ invite.email }}</span>
                                <button
                                    type="button"
                                    class="icon-btn"
                                    (click)="StartRename(invite)"
                                    [attr.aria-label]="'Rename ' + (invite.label || invite.roleName) + ' link for ' + company.companyName">
                                    ✎
                                </button>
                            </ng-container>
                            <input
                                *ngIf="renamingInviteId === invite.inviteId"
                                class="rename-input"
                                type="text"
                                maxlength="100"
                                [(ngModel)]="renameDraft"
                                [disabled]="isRenaming"
                                (keydown.enter)="SaveRename()"
                                (keydown.escape)="CancelRename()"
                                (blur)="SaveRename()"
                                mapsAutofocus />
                        </td>
                        <td>
                            {{ invite.roleName }}
                            <span *ngIf="invite.legacyTokenScoped" class="legacy-badge" title="Issued before the partner-account model; scoped to a legacy token">legacy</span>
                        </td>
                        <td>
                            <span class="status-pill" [class.revoked]="invite.status !== 'Active'">{{ invite.status }}</span>
                        </td>
                        <td>{{ invite.expiresAt | date: 'mediumDate' }}</td>
                        <td class="num">{{ invite.useCount }} / {{ invite.maxUses }}</td>
                        <td>{{ invite.createdAt ? (invite.createdAt | date: 'mediumDate') : '—' }}</td>
                        <td class="actions-col">
                            <button
                                *ngIf="invite.status === 'Active'"
                                type="button"
                                class="btn"
                                (click)="OpenRotate(company, invite)"
                                [attr.aria-label]="'Rotate ' + (invite.label || invite.roleName) + ' link for ' + company.companyName">
                                Rotate
                            </button>
                            <button
                                *ngIf="invite.status === 'Active'"
                                type="button"
                                class="btn danger"
                                (click)="OpenRevoke(company, invite)"
                                [attr.aria-label]="'Revoke ' + (invite.label || invite.roleName) + ' link for ' + company.companyName">
                                Revoke
                            </button>
                            <!-- Revoked links: remove from the list (hide-only — the
                                 invite row is never deleted from the database). -->
                            <button
                                *ngIf="invite.status === 'Revoked'"
                                type="button"
                                class="icon-btn trash-btn"
                                data-testid="hide-link"
                                (click)="OpenHide(company, invite)"
                                [attr.aria-label]="'Remove ' + (invite.label || invite.roleName) + ' link for ' + company.companyName + ' from the list'">
                                <svg viewBox="0 0 16 16" aria-hidden="true">
                                    <path d="M2.5 4h11M6.5 4V2.8h3V4M4 4l.8 9.2A1.2 1.2 0 0 0 6 14.3h4a1.2 1.2 0 0 0 1.2-1.1L12 4M6.6 6.6v5M9.4 6.6v5"
                                          fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </article>
    </section>
</div>

<!-- Create-link dialog: form view, then the show-once link view. -->
<dialog #createDialog class="partner-dialog" aria-labelledby="create-link-title" (close)="CloseCreateDialog()">
    <ng-container *ngIf="!createdLinkUrl">
        <h2 id="create-link-title">Create link{{ createFor ? ' — ' + createFor.companyName : '' }}</h2>
        <div class="dialog-field">
            <label for="create-link-name">Name — who is this link for?</label>
            <input
                id="create-link-name"
                name="createName"
                type="text"
                maxlength="100"
                placeholder="e.g. Alice (recruiting lead)"
                [(ngModel)]="createName"
                [disabled]="isCreatingLink" />
        </div>
        <div class="dialog-field">
            <label for="create-link-email">Email (optional)</label>
            <input
                id="create-link-email"
                name="createEmail"
                type="email"
                maxlength="150"
                placeholder="e.g. alice@partner.org"
                [(ngModel)]="createEmail"
                [disabled]="isCreatingLink" />
            <p class="field-hint">Candidate replies to messages sent from this link go to this address. Without one, replies land at the shared From inbox.</p>
        </div>
        <div class="dialog-field">
            <label for="create-link-role">Capability</label>
            <select id="create-link-role" name="createRole" [(ngModel)]="createRole" [disabled]="isCreatingLink">
                <option value="RECRUITER">Recruiter — search and export</option>
                <option value="ADMIN">Admin — dashboard, configuration, partner management</option>
            </select>
        </div>
        <div class="dialog-field">
            <label for="create-link-years">Expires in (years)</label>
            <input id="create-link-years" name="createYears" type="number" min="1" max="10" [(ngModel)]="createYears" [disabled]="isCreatingLink" />
        </div>
        <div class="dialog-actions">
            <button type="button" class="btn" (click)="CloseCreateDialog()" [disabled]="isCreatingLink">Cancel</button>
            <button type="button" class="btn primary" (click)="CreateLink()" [disabled]="isCreatingLink || createName.trim().length === 0">
                {{ isCreatingLink ? 'Creating…' : 'Create link' }}
            </button>
        </div>
    </ng-container>

    <ng-container *ngIf="createdLinkUrl">
        <h2 id="create-link-done-title">Link created — {{ createName || createdLinkRole }}</h2>
        <p class="show-once">This link is shown <strong>once</strong> — the server keeps only a hash. Copy it now and share it with the partner.</p>
        <code class="link-box" aria-label="Magic link URL">{{ createdLinkUrl }}</code>
        <div class="dialog-actions">
            <button type="button" class="btn" (click)="CloseCreateDialog()">Done</button>
            <button type="button" class="btn primary" (click)="CopyCreatedLink()">Copy link</button>
        </div>
    </ng-container>
</dialog>

<!-- Rotate dialog: confirm view, then the successor's show-once view. -->
<dialog #rotateDialog class="partner-dialog" aria-labelledby="rotate-title" (close)="CloseRotateDialog()">
    <ng-container *ngIf="!rotatedLinkUrl">
        <h2 id="rotate-title">Rotate this link?</h2>
        <p *ngIf="rotateTarget">
            This mints a replacement link for
            <strong>{{ rotateTarget.invite.label || rotateTarget.invite.roleName }}</strong>
            ({{ rotateTarget.company.companyName }}) and revokes the current one immediately —
            anyone using the old link loses access on their next request. The new link is shown once.
        </p>
        <div class="dialog-actions">
            <button type="button" class="btn" (click)="CloseRotateDialog()" [disabled]="isRotating">Cancel</button>
            <button type="button" class="btn primary" (click)="ConfirmRotate()" [disabled]="isRotating">
                {{ isRotating ? 'Rotating…' : 'Rotate link' }}
            </button>
        </div>
    </ng-container>

    <ng-container *ngIf="rotatedLinkUrl">
        <h2 id="rotate-done-title">New link — {{ rotatedLinkName }}</h2>
        <p class="show-once">The old link is revoked. This new link is shown <strong>once</strong> — copy it now and share it.</p>
        <code class="link-box" aria-label="New magic link URL">{{ rotatedLinkUrl }}</code>
        <div class="dialog-actions">
            <button type="button" class="btn" (click)="CloseRotateDialog()">Done</button>
            <button type="button" class="btn primary" (click)="CopyRotatedLink()">Copy link</button>
        </div>
    </ng-container>
</dialog>

<!-- Revoke confirm dialog. -->
<dialog #revokeDialog class="partner-dialog" aria-labelledby="revoke-title" (close)="CloseRevokeDialog()">
    <h2 id="revoke-title">Revoke this link?</h2>
    <p *ngIf="revokeTarget">
        The {{ revokeTarget.invite.roleName }} link for
        <strong>{{ revokeTarget.company.companyName }}</strong> stops working immediately —
        anyone signed in through it loses access on their next request. This cannot be undone.
    </p>
    <div class="dialog-actions">
        <button type="button" class="btn" (click)="CloseRevokeDialog()" [disabled]="isRevoking">Cancel</button>
        <button type="button" class="btn danger" (click)="ConfirmRevoke()" [disabled]="isRevoking">
            {{ isRevoking ? 'Revoking…' : 'Revoke link' }}
        </button>
    </div>
</dialog>

<!-- Hide confirm dialog (remove a revoked link from the list; never a DB delete). -->
<dialog #hideDialog class="partner-dialog" aria-labelledby="hide-title" (close)="CloseHideDialog()">
    <h2 id="hide-title">Remove this link from the list?</h2>
    <p *ngIf="hideTarget">
        The revoked {{ hideTarget.invite.roleName }} link
        <strong>{{ hideTarget.invite.label || '—' }}</strong> for
        <strong>{{ hideTarget.company.companyName }}</strong> disappears from this screen.
        Nothing is deleted — its record and history stay in the database.
    </p>
    <div class="dialog-actions">
        <button type="button" class="btn" (click)="CloseHideDialog()" [disabled]="isHiding">Cancel</button>
        <button type="button" class="btn primary" data-testid="hide-link-confirm" (click)="ConfirmHide()" [disabled]="isHiding">
            {{ isHiding ? 'Removing…' : 'Remove from list' }}
        </button>
    </div>
</dialog>
